import Link from 'next/link';
import { Card } from '@akademi/ui';
import type { ChannelCard as ChannelCardData } from '@akademi/site-builder-runtime';
import { cardReset } from '../_shared/styles';

export type ChannelCardVariant = 'detailed' | 'compact' | 'avatar-only';

export interface ChannelCardProps {
  channel: ChannelCardData;
  variant?: ChannelCardVariant;
  hrefTemplate?: string;
}

function compactCount(n: number | undefined): string {
  if (typeof n !== 'number') return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function ChannelCard({
  channel,
  variant = 'detailed',
  hrefTemplate = '/tube/channels/{slug}',
}: ChannelCardProps) {
  const href = hrefTemplate.replace('{slug}', encodeURIComponent(channel.slug));

  if (variant === 'avatar-only') {
    return (
      <Link href={href} title={channel.name} style={{ textDecoration: 'none' }}>
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: channel.avatarUrl
              ? `url(${channel.avatarUrl}) center/cover no-repeat`
              : 'var(--color-neutral-200, #e4e4e7)',
          }}
        />
      </Link>
    );
  }

  const isCompact = variant === 'compact';
  const metaLine = [
    channel.subscriberCount !== undefined
      ? `${compactCount(channel.subscriberCount)} subscribers`
      : null,
    channel.videoCount !== undefined ? `${compactCount(channel.videoCount)} videos` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card asChild>
      <Link
        href={href}
        style={{
          ...cardReset,
          display: 'flex',
          flexDirection: isCompact ? 'row' : 'column',
          alignItems: 'center',
          textAlign: isCompact ? 'left' : 'center',
          gap: isCompact ? 12 : 'var(--space-3, 12px)',
          textDecoration: 'none',
          color: 'inherit',
          padding: isCompact ? 12 : 'var(--space-5, 20px)',
          background: 'var(--color-surface-card, #fff)',
          borderRadius: 'var(--radius-lg, 16px)',
        }}
      >
        <div
          aria-hidden
          style={{
            width: isCompact ? 44 : 72,
            height: isCompact ? 44 : 72,
            borderRadius: '50%',
            background: channel.avatarUrl
              ? `url(${channel.avatarUrl}) center/cover no-repeat`
              : 'var(--color-surface-muted, #e4e4e7)',
            flexShrink: 0,
            boxShadow: 'inset 0 0 0 1px var(--color-border-default, rgba(0,0,0,0.06))',
          }}
        />
        <div
          style={{
            minWidth: 0,
            flex: isCompact ? 1 : undefined,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: isCompact ? 'flex-start' : 'center',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 600,
              color: 'var(--color-text-primary, #18181b)',
            }}
          >
            {channel.name}
          </div>
          {metaLine && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #9ca3af)',
              }}
            >
              {metaLine}
            </div>
          )}
          {variant === 'detailed' && channel.description && (
            <p
              style={{
                margin: '4px 0 0',
                fontSize: '13px',
                lineHeight: 1.5,
                color: 'var(--color-text-secondary, #71717a)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical' as const,
                overflow: 'hidden',
              }}
            >
              {channel.description}
            </p>
          )}
        </div>
      </Link>
    </Card>
  );
}
